<template>
  <VglGeometry   
    :draw-range-start="drawingFrom"
    :draw-range-count="drawingTo - drawingFrom"
  >
    <template #position>
      <VglFloat32Attribute
        :array="points"
        :item-size="3"
      />
    </template>
    <template 
      v-if="colorPattern"
      #color
    >
      <VglFloat32Attribute
        :array="colors"
        :item-size="3"
      />
    </template>
  </VglGeometry>
</template>

<script>
import * as d3 from 'd3'

import { 
  VglGeometry,
  VglFloat32Attribute,
} from 'vue-gl';

import { getNorm } from '@js/utils/TrajectoryUtil.js';

export default {
  components: {
    VglGeometry,
    VglFloat32Attribute,
  },
  props: {
    trajectory: {
      required: true,
    },
    selection: {
    },
    colorPattern: {
      default: null,
    },
  },
  computed: {
    startIndex: function(){
      return this.trajectory[0].index ?? 0;
    },
    drawingMax: function(){
      return this.trajectory.length;
    },
    drawingFrom: function(){
      let rtn = this.selection?.from ?? 0;
      rtn = parseInt(rtn) - this.startIndex;
      return rtn < 0 ? 0 : Math.min(this.drawingMax, rtn);
    },
    drawingTo: function(){
      let rtn = this.selection?.to ?? this.drawingMax;
      rtn = parseInt(rtn) - this.startIndex;
      return rtn < 0 ? 0 : Math.min(this.drawingMax, rtn);
    },
    points: function(){
      let rtn = [];
      this.trajectory.forEach(p => {
        rtn.push(p.x);
        rtn.push(p.y);
        rtn.push(p.z);
      });
      return rtn;
    },
    positions: function(){
      const rtn = [];
      this.trajectory.forEach(p => {
        rtn.push(getNorm({
          x: p.x,
          y: p.y,
          z: p.z,
        }));
      });
      return rtn;
    },
    minPosition: function(){
      return Math.min(...this.positions);
    },
    maxPosition: function(){
      return Math.max(...this.positions);
    },
    velocities: function(){
      const rtn = [];
      let previousPoint = {
        x: 0,
        y: 0,
        z: 0
      }
      this.trajectory.forEach(p => {
        rtn.push(getNorm({
          x: p.x - previousPoint.x,
          y: p.y - previousPoint.y,
          z: p.z - previousPoint.z,
        }));

        previousPoint = p;
      });
      return rtn;
    },
    sortedVelocities: function(){
      const rtn = [...this.velocities];
      rtn.sort();
      return rtn;      
    },
    minVelocity: function(){
      return this.sortedVelocities[0];
    },
    maxVelocity: function(){
      return this.sortedVelocities[parseInt(this.sortedVelocities.length - 2)];
    },
    accelerations: function(){
      const rtn = [];
      let previousPoint = {
        x: 0,
        y: 0,
        z: 0
      }
      let previousVelocity = {
        x: 0,
        y: 0,
        z: 0
      };
      this.trajectory.forEach(p => {
        const velocity = {
          x: p.x - previousPoint.x,
          y: p.y - previousPoint.y,
          z: p.z - previousPoint.z,
        }

        rtn.push(getNorm({
          x: velocity.x - previousVelocity.x,
          y: velocity.y - previousVelocity.y,
          z: velocity.z - previousVelocity.z,
        }));

        previousPoint = p;
        previousVelocity = velocity;
      });
      return rtn;
    },
    sortedAccelerations: function(){
      const rtn = [...this.accelerations];
      rtn.sort();
      return rtn;      
    },
    minAcceleration: function(){
      return this.sortedAccelerations[0];
    },
    maxAcceleration: function(){
      return this.sortedAccelerations[this.sortedAccelerations.length - 3];
    },
    getColor: function(){
      //MEMO https://wizardace.com/d3v5-scale-chromatic/
      return value => {
        const v = (value - this.colorMinValue)/this.colorValueRange;
        return d3.interpolateSpectral(
          Math.max(
            Math.min(1, 1 - v), 
            0
          )
        );
      }
    },
    colorValueRange: function(){
      switch(this.colorPattern){
        case 'position':
          return this.maxPosition - this.minPosition;
        case 'velocity':
          return this.maxVelocity - this.minVelocity;
        case 'acceleration':
          return this.maxAcceleration - this.minAcceleration;
      }
      return 1;
    },
    colorMinValue: function(){
      switch(this.colorPattern){
        case 'position':
          return this.minPosition;
        case 'velocity':
          return this.minVelocity;
        case 'acceleration':
          return this.minAcceleration;
      }
      return 0;
    },
    colorValues: function(){
      switch(this.colorPattern){
        case 'position':
          return this.positions;
        case 'velocity':
          return this.velocities;
        case 'acceleration':
          return this.accelerations;
      }
      return [];
    },
    colors: function(){
      const rtn = [];      
      this.colorValues.forEach(v => {
        const color = this.getColor(v).replace('rgb(', '').replace(')', '');
        const values = color.split(',').map(c => parseInt(c)/255);
        rtn.push(values[0]);
        rtn.push(values[1]);
        rtn.push(values[2]);
      });
      return rtn;
    },
  },
}
</script>